import React, { useEffect, useState } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import BrandMark from "../components/BrandMark";
import { api } from "../lib/api";
import { FadeUp } from "../components/Motion";
import SEO, { PersonSchema, BreadcrumbSchema } from "../components/SEO";

// Hardcoded updated bios/labels per Oct 2026 content update.
// Server-seeded data is overridden by name so client always wins.
const OVERRIDES = {
  "Col. (Ret.) Bob Carpenter, P.E.": {
    photo_url: "/images/team/bob-carpenter.jpg",
    photo_position: "center top",
    title: "Founder, President & CEO",
    quote: "The job is to understand the problem, make a clear plan, and follow it through.",
    bio: "Bob founded BCPeabody in 2006 after 25 years of active Army service, retiring as a Colonel and Commander of the U.S. Army Corps of Engineers Jacksonville District, where he led about 980 people and a $475 million annual program across Florida and the Caribbean. He sets the firm's direction, makes its final decisions and personally leads the expert witness practice.",
    attributes: [
      "Professional Engineer, Florida, Virginia and Puerto Rico",
      "Florida Certified General Contractor",
      "LEED Accredited Professional",
      "M.S. Civil Engineering, Stanford University",
      "M.A. Strategic Studies, U.S. Army War College",
      "B.S., U.S. Military Academy (West Point)",
      "Former Associate Professor of Civil Engineering, West Point",
      "Commissioner, Florida Century Commission (appointed by the Governor)",
      "Available for deposition and trial testimony",
    ],
  },
  "Kimberly Carpenter": {
    title: "Chief Operating Officer",
    quote: null,
    active: true,
    category: "leadership",
    bio: "Kimberly runs BCPeabody's project operations and client delivery. She started with the firm in 2013 in operations, estimating and purchasing on custom homes, commercial build-outs, additions and remodels, and managed a remodeling territory with as many as 42 projects underway at once.\n\nShe went on to lead the firm's FEMA Public Assistance work in Puerto Rico and Florida as Vice President and then Executive Vice President, covering more than 150 grants with over $300 million in estimated value. From 2023 to 2026 she worked with a national disaster recovery consulting firm, managing FEMA Public Assistance recovery for multiple public agencies after Hurricane Ida: more than 50 projects worth over $300 million, including about $100 million in hazard mitigation proposals.",
    attributes: [
      "13+ years in construction and disaster recovery",
      "Estimating, purchasing and project operations",
      "FEMA Public Assistance and Section 406 hazard mitigation",
      "2 CFR 200 procurement and cost reasonableness",
      "M.A., University of South Florida",
      "B.A., Political Science, Presbyterian College",
    ],
  },
  "Darrell Atkins": {
    title: "Construction Division Director",
    quote: "Every detail matters. Every day.",
    bio: "Darrell has more than 30 years in construction, including 20 years running his own custom home building company in North Carolina. He joined BCPeabody in 2014 as a construction superintendent on custom homes and remodels, then led FEMA-funded reconstruction scoping and estimating after Hurricanes Irma, María and Ian and the Puerto Rico earthquakes, completing hundreds of damage assessments along the way. Today he runs field operations: crews, trades, schedule and quality on every project. U.S. Navy veteran.",
  },
  "David Seale": {
    title: "Senior Superintendent",
    bio: "David has been with BCPeabody for more than ten years and has completed more than 100 kitchen and bathroom remodels. A master cabinet craftsman, he is known for detail, finish quality and clean framing-to-trim execution. After Hurricanes Irma and María he joined the firm's disaster recovery teams, completing more than 50 damage assessments for FEMA Public Assistance projects.",
  },
};

const KIM_FALLBACK = {
  id: "kimberly-carpenter",
  name: "Kimberly Carpenter",
  photo_url: "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/qxlydxdj_image.png",
};

function applyOverride(m) {
  const o = OVERRIDES[m.name];
  if (!o) return m;
  return { ...m, ...o };
}

export default function Team() {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team").then((r) => {
      const list = r.data.some((m) => m.name === "Kimberly Carpenter") ? r.data : [...r.data, KIM_FALLBACK];
      setTeam(list.map(applyOverride));
    });
  }, []);

  const visible = team.filter((m) => m.active !== false);
  // Force Bob first, Kim second regardless of API order.
  const LEAD_ORDER = { "Col. (Ret.) Bob Carpenter, P.E.": 0, "Kimberly Carpenter": 1 };
  const leadership = visible
    .filter((m) => m.category === "leadership")
    .sort((a, b) => (LEAD_ORDER[a.name] ?? 99) - (LEAD_ORDER[b.name] ?? 99));
  const field = visible.filter((m) => m.category === "field");

  return (
    <>
      <SEO title="Leadership & Team | BCPeabody Construction Services" description="Meet Col. (Ret.) Bob Carpenter, P.E., Founder, President & CEO; Kimberly Carpenter, COO; and our field superintendents." path="/team" />
      <BreadcrumbSchema items={[{ name: "Home", url: "https://bcpeabody.com/" }, { name: "Team", url: "https://bcpeabody.com/team" }]} />
      <PersonSchema name="Robert M. Carpenter" jobTitle="Founder, President & CEO" description="Former Commander of the U.S. Army Corps of Engineers Jacksonville District. West Point graduate and Stanford-trained civil engineer." alumniOf={["United States Military Academy", "Stanford University"]} credentials={["Florida Professional Engineer PE 63780", "Florida Certified General Contractor CGC1515086"]} />
      <PersonSchema name="Kimberly Carpenter" jobTitle="Chief Operating Officer" description="Runs project operations and client delivery for BCPeabody." alumniOf={["University of South Florida"]} />
      <Nav />

      <section className="pt-32 pb-14 bg-cream-light">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.18em] uppercase mb-5 text-copper">Our Team</div>
          <h1 className="font-serif font-medium text-ink tracking-tight leading-[1.05]" style={{ fontSize: "clamp(34px, 5vw, 60px)" }}>
            The people on your project.
          </h1>
          <p className="mt-6 text-ink/75 max-w-2xl" style={{ fontSize: 17 }}>
            A small team by design. The people you meet at the start are the people who run your job.
          </p>
        </div>
      </section>

      {/* Leadership */}
      <section data-testid="team-leadership" className="bg-cream py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.18em] uppercase mb-10 text-copper">Leadership</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {leadership.map((m, i) => (
              <FadeUp key={m.id} delay={i * 100}>
                <article className="group" data-testid={`team-card-${m.name.split(" ").slice(-2).join("-").toLowerCase().replace(/[^a-z-]/g, "")}`}>
                  <div className="relative h-[480px] overflow-hidden bg-ink mb-6">
                    <img src={m.photo_url} alt={m.is_placeholder_photo ? "Temporary team portrait" : m.name} style={{ objectPosition: m.photo_position || "center" }} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-2">{m.title}</div>
                  <h3 className="font-serif text-3xl text-ink mb-5 tracking-tight">{m.name}</h3>
                  {m.quote && <p className="font-serif text-xl text-ink/85 leading-snug mb-5 border-l-2 border-copper pl-5">"{m.quote}"</p>}
                  {m.bio && m.bio.split("\n\n").map((para, pi) => (
                    <p key={pi} className="text-ink/75 leading-relaxed mb-4">{para}</p>
                  ))}
                  {m.attributes && m.attributes.length > 0 && (
                    <ul className="space-y-2 mt-5">
                      {m.attributes.map((a) => (
                        <li key={a} className="text-[12px] tracking-[0.08em] text-ink/70 flex items-start gap-3">
                          <span className="w-4 h-px bg-copper mt-2.5 shrink-0" /> <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Field */}
      <section data-testid="team-field" className="bg-ink text-cream-light py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.18em] uppercase mb-4 text-copper-light">Field and Construction</div>
          <h2 className="font-serif font-medium mb-5 tracking-tight" style={{ fontSize: "clamp(28px, 3.6vw, 40px)" }}>Field-led, every project.</h2>
          <p className="text-cream-light/75 max-w-2xl mb-12 leading-relaxed">
            Every BCPeabody project is run on site by an experienced superintendent, not handed off or loosely managed.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {field.map((m, i) => (
              <FadeUp key={m.id} delay={i * 100}>
                <article className="border border-copper/25 p-7 h-full">
                  {m.photo_url ? (
                    <div className="relative h-64 overflow-hidden mb-5">
                      <img src={m.photo_url} alt={m.name} style={{ objectPosition: m.photo_position || "center" }} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  ) : (
                    <div className="relative h-64 mb-5 flex items-center justify-center border border-copper/25 bg-ink/40">
                      <BrandMark variant="icon" linked={false} size="lg" tone="light" />
                    </div>
                  )}
                  <div className="text-[11px] tracking-[0.16em] uppercase text-copper-light mb-2">{m.title}</div>
                  <h3 className="font-serif text-2xl mb-4 tracking-tight">{m.name}</h3>
                  {m.quote && <p className="font-serif italic text-lg text-cream-light/85 mb-4">"{m.quote}"</p>}
                  <p className="text-cream-light/75 text-sm leading-relaxed">{m.bio}</p>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
